import { RestClient, RequestResponse } from "./base/rest.client";
import { CommonDto } from "../common.dto";
import { BASE_URL } from "../config";
import * as z from "zod";

export interface FreePrint {
  service_type: string;
  variant: string | null;
  resource: string;
  count: number;
}

/** Строка каталога наград — показывается и с нулём. */
export interface CatalogItem {
  resource: string;
  title: string;
  service_type: string;
  variant: string | null;
  count: number;
}

export interface RedeemResult {
  redeemed: boolean;
  kind: string | null;
  resource: string | null;
  amount: number;
  title: string | null;
  reason: string | null;
  money: number;
  prints: FreePrint[];
  catalog: CatalogItem[];
}

export interface WalletEntry {
  kind: string;
  service_type: string | null;
  amount: number;
  source: string;
  comment: string | null;
  created_at: string | null;
  is_canceled: boolean;
}

export interface Wallet {
  money: number;
  prints: FreePrint[];
  catalog: CatalogItem[];
  entries: WalletEntry[];
}

export interface WelcomeGift {
  granted: boolean;
  label: string | null;
  kind: string | null;
  service_type: string | null;
  amount: number;
  granted_at: string | null;
  reason: string | null;
}

const PrintSchema = z.object({
  service_type: z.string(),
  variant: z.string().nullable().default(null),
  resource: z.string().default(""),
  count: z.number(),
});

const CatalogSchema = z.object({
  resource: z.string(),
  title: z.string(),
  service_type: z.string(),
  variant: z.string().nullable().default(null),
  count: z.number(),
});

const RedeemSchema = z.object({
  redeemed: z.boolean(),
  kind: z.string().nullable().default(null),
  resource: z.string().nullable().default(null),
  amount: z.number().default(0),
  title: z.string().nullable().default(null),
  reason: z.string().nullable().default(null),
  money: z.number().default(0),
  prints: z.array(PrintSchema).default([]),
  catalog: z.array(CatalogSchema).default([]),
});

const WalletSchema = z.object({
  money: z.number(),
  prints: z.array(PrintSchema),
  catalog: z.array(CatalogSchema).default([]),
  entries: z.array(
    z.object({
      kind: z.string(),
      service_type: z.string().nullable(),
      amount: z.number(),
      source: z.string(),
      comment: z.string().nullable(),
      created_at: z.string().nullable(),
      is_canceled: z.boolean(),
    })
  ),
});

const WelcomeSchema = z.object({
  granted: z.boolean(),
  label: z.string().nullable(),
  kind: z.string().nullable(),
  service_type: z.string().nullable().optional(),
  amount: z.number().optional(),
  granted_at: z.string().nullable(),
  reason: z.string().nullable().optional(),
});

class WalletClientClass extends RestClient {
  baseUrl = BASE_URL;

  async getWallet(): Promise<RequestResponse<Wallet>> {
    const result = await this.request("GET", "/api/front/wallet");
    return this.validateResponse(result, CommonDto.withStatus(WalletSchema));
  }

  async redeem(code: string): Promise<RequestResponse<RedeemResult>> {
    const result = await this.request("POST", "/api/front/wallet/redeem", {
      jsonPayload: { code },
    });
    return this.validateResponse(result, CommonDto.withStatus(RedeemSchema));
  }

  async getWelcome(): Promise<RequestResponse<WelcomeGift>> {
    const result = await this.request("GET", "/api/front/welcome");
    return this.validateResponse(result, CommonDto.withStatus(WelcomeSchema));
  }

  async claimWelcome(touchpointCode: string): Promise<RequestResponse<WelcomeGift>> {
    const result = await this.request("POST", "/api/front/welcome/claim", {
      jsonPayload: { touchpoint_code: touchpointCode },
    });
    return this.validateResponse(result, CommonDto.withStatus(WelcomeSchema));
  }
}

export class WalletClient extends WalletClientClass {}
